import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { useCrudEditContext } from "@ryde/redux";
import {
  DirectionsServiceEnum,
  formatRebuildRouteAppliedInfo,
  ICourseDto,
  isCustomRebuildRouteType,
  lastRouteRebuildAtAtom,
  lastSelectedRebuildTypeAtom,
  resolveDirectionsServiceLabel,
  resolveInitialRebuildRouteProvider,
  useFormats,
  useSystemParams,
} from "@ryde/shared";

export const useCourseEditTopInfo = () => {
  const { t } = useTranslation();
  const { formatDateTimeWithSecondsNormalized } = useFormats();
  const systemParams = useSystemParams();
  const {
    data: { dto: courseDto },
  } = useCrudEditContext<ICourseDto>();
  const lastSelectedRebuildType = useRecoilValue(lastSelectedRebuildTypeAtom);
  const lastRouteRebuildAt = useRecoilValue(lastRouteRebuildAtAtom);

  const rebuildTypeBannerText = useMemo(() => {
    if (!isCustomRebuildRouteType(lastSelectedRebuildType)) {
      return undefined;
    }

    const provider = resolveInitialRebuildRouteProvider({
      lastUsedProvider: courseDto?.metadata?.directionsService,
      systemDefaultProvider:
        systemParams.directionsService || DirectionsServiceEnum.Google,
    });
    const providerLabel = resolveDirectionsServiceLabel(provider) ?? provider;
    const appliedAtLabel = lastRouteRebuildAt
      ? formatDateTimeWithSecondsNormalized(lastRouteRebuildAt)
      : undefined;

    return formatRebuildRouteAppliedInfo({
      providerLabel,
      provider,
      appliedAtLabel,
      rebuildRouteType: lastSelectedRebuildType,
      t,
    });
  }, [
    courseDto?.metadata?.directionsService,
    formatDateTimeWithSecondsNormalized,
    lastRouteRebuildAt,
    lastSelectedRebuildType,
    systemParams.directionsService,
    t,
  ]);

  return { rebuildTypeBannerText };
};
